import axios from 'axios';
import { ErrorNotification } from '@/store/modules/notifications/notifications';
import {
  makeHierarchy,
  getCohortSubjectIds,
  hexToRGB,
  remapAudBasePureToneNoResponseValues,
} from '@/utils/helpers';
import { actions, mutations, state as stateTypes } from './types';

export default {
  async [actions.FETCH_COLLECTION]({ commit, dispatch }, collectionId) {
    commit(mutations.SET_LOADING, true);

    try {
      const { data } = await axios.get(
        `/api/collections/${collectionId}?include=studies&include=variables&include=cohorts`
      );

      // massage collection data... here
      data.collection.subject_variables_list =
        data.collection.subject_variables;
      var c = data.collection;
      c.date_generated_epoch = new Date(c.date_generated).getTime();
      c.last_modified_epoch = new Date(c.last_modified).getTime();
      c.has_visits_set = true;
      c.observation_variables.forEach(v => {
        if (
          (v.first_visit_event == null && v.first_visit_num == null) ||
          (v.last_visit_event == null && v.last_visit_num == null)
        ) {
          c.has_visits_set = false;
        }
      });

      const subjectVariables = makeHierarchy(data.collection.subject_variables);
      subjectVariables.forEach(subjectVariable => {
        subjectVariable.children.forEach(child => {
          child.type = 'subject';
        });
      });

      // determine whether this is longitudinal or cross-sectional data
      data.collection.is_longitudinal = true;
      data.collection.studies.forEach(s => {
        if (s.study.longitudinal === 0) data.collection.is_longitudinal = false;
      });

      const observationVariables = makeHierarchy(
        data.collection.observation_variables
      );

      observationVariables.forEach(observationVariable => {
        observationVariable.children.forEach(child => {
          child.type = 'observation';
          if (data.collection.is_longitudinal) {
            child.is_longitudinal = true;
          } else {
            child.is_longitudinal = false;
          }
        });
      });

      data.collection.observation_variables_list =
        data.collection.observation_variables;
      data.collection.subject_variables = subjectVariables;
      data.collection.observation_variables = observationVariables;
      commit(mutations.SET_COLLECTION, data.collection);
      dispatch(actions.SET_COHORT_SUBJECTS);
    } catch (err) {
      const notification = new ErrorNotification(err);
      dispatch(notification.dispatch, notification, { root: true });
    } finally {
      commit(mutations.SET_LOADING, false);
    }
  },
  async [actions.CLEAR_DATA]({ commit }) {
    commit(mutations.SET_DATA, []);
    commit(mutations.SET_RAW_DATA, []);
    commit(mutations.SET_COHORTS, []);
    commit(mutations.SET_VISIBLE_COHORTS, []);
  },
  async [actions.FETCH_DATA]({ commit, dispatch, state }) {
    commit(mutations.SET_LOADING, true);
    const collection = state[stateTypes.COLLECTION];
    try {
      const response = await axios.get(
        `/api/cohort-manager?collection=${collection.id}` +
          (collection.is_longitudinal ? '&include=raw_data' : '')
      );
      const data = response.data;
      remapAudBasePureToneNoResponseValues(collection, data.data);
      commit(mutations.SET_DATA, data.data);
      if (collection.is_longitudinal) {
        commit(mutations.SET_RAW_DATA, data.raw_data);
      }
    } catch (err) {
      const notification = new ErrorNotification(err);
      dispatch(notification.dispatch, notification, { root: true });
    } finally {
      commit(mutations.SET_LOADING, false);
    }
  },
  [actions.SET_OUTCOME_VARIABLES]({ commit }, outcomeVariables) {
    commit(mutations.SET_OUTCOME_VARIABLES, outcomeVariables);
  },
  [actions.SET_DETAILED_VIEW]({ commit }, detailedView) {
    commit(mutations.SET_DETAILED_VIEW, detailedView);
  },
  [actions.SET_PVAL_THRESHOLD]({ commit }, threshold) {
    commit(mutations.SET_PVAL_THRESHOLD, threshold);
  },
  [actions.RESET_PVAL_THRESHOLD]({ commit }) {
    commit(mutations.SET_PVAL_THRESHOLD);
  },
  async [actions.FETCH_COHORTS]({ commit, dispatch }, collectionId) {
    commit(mutations.SET_LOADING, true);

    try {
      const { data } = await axios.get(
        `/api/collections/${collectionId}/cohorts`
      );
      // new - set outcome-based analysis mode
      if (
        data.cohorts.length > 0 &&
        data.cohorts[data.cohorts.length - 1].label.startsWith('[O]_')
      ) {
        commit(mutations.SET_ANALYSIS_MODE, 'Outcome based');
      } else {
        commit(mutations.SET_ANALYSIS_MODE, 'Predictor based');
      }
      commit(mutations.SET_COHORTS, data.cohorts);
    } catch (err) {
      const notification = new ErrorNotification(err);
      dispatch(notification.dispatch, notification, { root: true });
    } finally {
      commit(mutations.SET_LOADING, false);
    }
    dispatch(actions.SET_COHORT_SUBJECTS);
  },
  [actions.SET_VISIBLE_COHORTS]({ commit }, cohorts) {
    commit(mutations.SET_VISIBLE_COHORTS, cohorts);
  },
  async [actions.ANALYZE_COHORTS]({ commit, dispatch, state }, cohorts) {
    const { data } = state;
    let { outputVariables } = state;
    const collection = state[stateTypes.COLLECTION];
    const { selectedAnalysisMode } = state;

    // need cohorts, collection, and output vars
    if (
      typeof collection === 'undefined' ||
      typeof collection.observation_variables === 'undefined' ||
      typeof cohorts === 'undefined'
    ) {
      commit(mutations.SET_ANOVA_PVALS, []);
      return;
    }

    // create group of samples for each cohort:
    const groups = [];
    cohorts.forEach(c => {
      if (c.collection_id === collection.id) {
        const subjids = [];
        c.subject_ids.forEach(sid => {
          subjids[sid] = 1;
        });
        const cohortData = data.filter(d => d.subject_id in subjids);
        groups.push(cohortData);
      }
    });
    const numGroups = groups.length;

    // pass _all_ observation variables, not just the selected ones
    const outputVars = [];
    collection.observation_variables.forEach(v => {
      v.children.forEach(c => {
        outputVars.push(c);
      });
    });
    if (selectedAnalysisMode == 'Outcome based') {
      collection.subject_variables.forEach(v => {
        v.children.forEach(c => {
          outputVars.push(c);
        });
      });
    }
    outputVariables = outputVars;

    const comparisonField = 'change';

    try {
      const input = {
        numGroups,
        groups,
        outputVariables,
        comparisonField,
      };
      const { data } = await axios.post(`/api/compute-anova`, input);
      commit(mutations.SET_ANOVA_PVALS, data.pvals);
      commit(mutations.SET_ANOVA_PVALS_INPUT, input);
    } catch (err) {
      const notification = new ErrorNotification(err);
      dispatch(notification.dispatch, notification, { root: true });
    }
  },
  [actions.SET_COHORT_SUBJECTS]({ state }) {
    const { cohorts, collection, data } = state;
    if (
      typeof collection === 'undefined' ||
      typeof cohorts === 'undefined' ||
      typeof data === 'undefined' ||
      data.length === 0
    ) {
      return;
    }
    // compute subjects in each cohort
    cohorts.forEach(c => {
      if (c.collection_id === collection.id) {
        const subjIds = getCohortSubjectIds(data, c);
        c.subject_ids = subjIds;
      }
    });
  },
  async [actions.SAVE_COHORT_DISPLAY_PREFS]({ dispatch }, cohort_prefs) {
    let cPrefs = cohort_prefs.map(cp => {
      let cpref = { ...cp };
      cpref.color = hexToRGB(cp.color);
      return cpref;
    });
    try {
      await axios.post(`/api/cohorts/display_preferences`, {
        display_prefs: cPrefs,
      });
    } catch ({ response }) {
      const notification = new ErrorNotification(
        'Failed to update cohort display preferences'
      );
      dispatch(notification.dispatch, notification, { root: true });
    }
  },
  [actions.SET_SCREEN_RESIZE]({ commit }, input) {
    commit(mutations.SET_SCREEN_RESIZE, input);
  },
  // new - outcome based analysis
  [actions.SET_ANALYSIS_MODE]({ commit }, mode) {
    commit(mutations.SET_ANALYSIS_MODE, mode);
  },
};
